import React, { FC, useEffect, useState } from 'react';
import { useGetAllClientsQuery } from '../../api/clientAPI';
import { BASE_URL } from '../../api/config';
import { useSendToBroadcastersMutation } from '../../api/projectsApi';
import AsyncImage from '../../components/AsyncImage/AsyncImage';
import Form from '../../components/Form/Form';
import Icon from '../../components/Icon/Icon';
import { H2, P, Span } from '../../components/Text/Text';
import Tooltip from '../../components/Tooltip/Tooltip';
import { ClientTypeE } from '../../models/enums';
import { BroadcasterT, ClientT, FormFieldT } from '../../models/models';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectShareToken, selectUserMailSharedProject } from '../../store/selectors/appSelectors';
import { toggleLoading } from '../../store/slices/appSlice';
import getImageLink from '../../utils/helpers/getImageLink';
import usePopup from '../../utils/hooks/usePopup';
import SelectBroadcasters from '../SelectBroadcasters';
import SendToBroadcasterSuccess from '../SendToBroadcasterSuccess/SendToBroadcasterSuccess';

import styles from './sendToBroadcasters.module.scss';

type SendToBroadcastersBodyPropTypes = {
    broadcasters: BroadcasterT[];
    campaignId: number;
    deliveryNumber: number;
    id: number;
    refetch: () => void;
    errors: {
        name: string,
        incorrectLength: boolean,
        resolutionError: boolean,
        encodingError: boolean,
    }[],
};

const SendToBroadcastersBody: FC<SendToBroadcastersBodyPropTypes> = (props) => {
    const { broadcasters, campaignId, id, deliveryNumber, refetch, errors } = props;
    const fields: FormFieldT[] = [
        { label: 'Comment' }
    ];
    const [allDestinations, setAllDestinations] = useState([]);
    const shareToken = useAppSelector(selectShareToken);
    const UserMailSharedProject = useAppSelector(selectUserMailSharedProject);
    const { data: clients } = useGetAllClientsQuery({ shareToken });
    const { createPopup, removePopup } = usePopup();
    const [selectedDestinations, setSelectedDestinations] = useState(broadcasters);
    const [sendToBroadcasters] = useSendToBroadcastersMutation();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (clients) {
            const broadcasterClients = clients
                .filter((client: ClientT) => client.ClientType === ClientTypeE.Broadcaster);
            if (broadcasterClients?.length) {
                setAllDestinations(broadcasterClients);
            }
        }
    }, [clients]);

    const handleSubmit = async (data: any) => {
        const body = {
            Id: campaignId,
            ExtraText: data.comment,
            BroadcasterIds: selectedDestinations?.map(({ Id }) => Id).join(',') || '',
            CompanyName: null,
            FullName: null,
            SharedProjectViewModel: {
                AdvertiserEmail: UserMailSharedProject,
                ShareToken: shareToken,
            },
        };

        try {
            const res = await sendToBroadcasters(body);
            dispatch(toggleLoading());
            await refetch();
            removePopup();
            dispatch(toggleLoading());
            createPopup({
                body: <SendToBroadcasterSuccess Id={id} DeliveryNumber={deliveryNumber} />
            });
        } catch (e: any) {
            console.log(e);
        }
    };

    const handleSelectBroadcasters = () => {
        const handleSelect = (selected: number[]) => {
            const newDestinations = allDestinations.filter(({ Id }) => selected.some(id => +id === +Id))
            setSelectedDestinations(newDestinations);
        };
        createPopup(SelectBroadcasters(allDestinations, selectedDestinations, handleSelect, removePopup));
    };

    return errors.length ? (
        <div className={styles.errorsWrapper}>
            {errors.some(error => error.encodingError || error.resolutionError || error.incorrectLength) && (
                <p className={styles.error}>
                    <Icon name='exclamation' className={styles.errorIcon} />
                    <Span>
                        Attention: some versions have actual lengths that differ from their required lengths.
                    </Span>
                </p>
            )}
            {/* {errors.some(error => error.resolutionError) && (
                <p className={styles.error}>
                    <Icon name='exclamation' className={styles.errorIcon} />
                    <Span>
                        It is not possible to approve the delivery of these versions for broadcasting. Make sure that all versions are in accordance with the technical requirements of the instructions for closing the video files and do not have length problems or extra frames that do not match the version settings.
                    </Span>
                </p>
            )} */}
        </div>
    ) : (
        <div>
            <P className={styles.notice}>
                Notice: By sending the project you approve the project for broadcasting.
                E-mail with a link for downloading project videos will be sent to the following companies.
                Upon receiving the email, recipients will be able to download video files for the project.
            </P>
            <H2 className={styles.subTitle}>Destinations:</H2>
            <ul className={styles.destinationList}>
                {selectedDestinations?.map((item, index) => (
                    <Tooltip text={item.Name} delayShow={100}>
                        <li key={index}>
                            <AsyncImage link={getImageLink({ id: item.Id, shareToken })} />
                        </li>
                    </Tooltip>
                ))}
                <li onClick={handleSelectBroadcasters} className={styles.addItem}>+</li>
            </ul>
            <Form
                fields={fields}
                formID='send-to-broadcasters'
                onSubmit={handleSubmit}
                columns={1}
                className={styles.extraText}
            />
        </div>
    );
};

export default SendToBroadcastersBody;